import { MenuDataTyping } from '@/services/userInterface';

const parseBooleanValue = (value: unknown, defaultValue: boolean) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (['true', '1', 'y', 'yes'].includes(normalizedValue)) {
      return true;
    }
    if (['false', '0', 'n', 'no'].includes(normalizedValue)) {
      return false;
    }
  }
  return defaultValue;
};

export const isMenuLocked = (menu?: Partial<MenuDataTyping>) => {
  if (!menu) {
    return false;
  }

  const locked = parseBooleanValue(menu.locked, false);
  const authorized = parseBooleanValue(menu.authorized, true);
  const menuLocked = parseBooleanValue(menu.menuLocked, false);

  return menuLocked || locked || !authorized;
};

export const normalizeMenuAccess = <T extends Record<string, any>>(
  menus?: T[],
  parentLocked = false,
): T[] => {
  if (!Array.isArray(menus)) {
    return [];
  }

  return menus.map((menu) => {
    const menuLocked = parentLocked || isMenuLocked(menu);
    const normalizedMenu = {
      ...menu,
      menuLocked,
      disabled: parseBooleanValue(menu.disabled, false),
    };

    if (Array.isArray(menu.routes)) {
      normalizedMenu.routes = normalizeMenuAccess(menu.routes, menuLocked);
    }

    if (Array.isArray(menu.children)) {
      normalizedMenu.children = normalizeMenuAccess(menu.children, menuLocked);
    }

    return normalizedMenu;
  });
};

const normalizePath = (path?: string) => {
  if (!path || path.startsWith('http')) {
    return '';
  }

  const pathname = path.split(/[?#]/)[0];
  const normalizedPath = `/${pathname}`.replace(/\/+/g, '/');

  if (normalizedPath.length > 1) {
    return normalizedPath.replace(/\/$/, '');
  }

  return normalizedPath;
};

const matchMenuPath = (menuPath?: string, currentPath?: string) => {
  const routePath = normalizePath(menuPath);
  const urlPath = normalizePath(currentPath);

  if (!routePath || !urlPath) {
    return false;
  }

  if (routePath.endsWith('/*')) {
    return urlPath.startsWith(routePath.replace(/\/\*$/, ''));
  }

  if (routePath === urlPath) {
    return true;
  }

  const routeParts = routePath.split('/');
  const urlParts = urlPath.split('/');

  if (routeParts.length !== urlParts.length) {
    return false;
  }

  return routeParts.every((routePart, index) => {
    return routePart.startsWith(':') || routePart === '*' || routePart === urlParts[index];
  });
};

export const findLockedMenuByPath = <T extends Record<string, any>>(
  menus?: T[],
  currentPath?: string,
  parentLocked = false,
): T | undefined => {
  if (!Array.isArray(menus)) {
    return undefined;
  }

  for (const menu of menus) {
    const menuLocked = parentLocked || isMenuLocked(menu);

    if (menuLocked && matchMenuPath(menu.path, currentPath)) {
      return menu;
    }

    const lockedChild = findLockedMenuByPath(
      Array.isArray(menu.routes) ? menu.routes : menu.children,
      currentPath,
      menuLocked,
    );

    if (lockedChild) {
      return lockedChild;
    }
  }

  return undefined;
};

// 获取打平后的后端配置的路由表 （tableTreeForAntPro）
export const getHorRoutes = (routes: MenuDataTyping[]) => {
  const flattenRoutes = (routes: MenuDataTyping[]): MenuDataTyping[] => {
    let flattenedRoutes: MenuDataTyping[] = [];

    routes.forEach((route) => {
      flattenedRoutes.push(route);

      if (route.routes && route.routes.length > 0) {
        const nestedRoutes = flattenRoutes(route.routes);
        flattenedRoutes = flattenedRoutes.concat(nestedRoutes);
      }
    });

    return flattenedRoutes;
  };

  return flattenRoutes(routes);
};

export const checkRouteAccess = (routeTable: string[], currentUrl: string) => {
  return routeTable.some((routePattern) => {
    // 将路由模式和当前 URL 切分为部分
    const routeParts = routePattern.split('/');
    const urlParts = currentUrl.split('/');

    // 如果部分数量不同，则无法匹配
    if (routeParts.length !== urlParts.length) {
      return false;
    }

    // 检查所有部分是否匹配
    for (let i = 0; i < routeParts.length; i++) {
      const routePart = routeParts[i];
      const urlPart = urlParts[i];

      // 如果路由部分是动态参数，忽略它，继续下一个部分的比较
      if (routePart.startsWith(':')) {
        continue;
      }

      // 如果固定部分不匹配，则整个路由不匹配
      if (routePart !== urlPart) {
        return false;
      }
    }

    // 如果所有部分都匹配（考虑动态参数），则路由匹配
    return true;
  });
};
